import isEqual from "lodash/isEqual";
import uniqWith from "lodash/unionWith";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, ButtonGroup, CardTitle, Row } from "reactstrap";
import PriceCard from "/@src/components/cards/PriceCard";
import { Colxx } from "/@src/components/common/CustomBootstrap";
import { appRoot } from "/@src/constants/defaultValues";
import { getMappedPlansAndFeatures } from "/@src/constants/plansAndFeatures";
import FeatureComparison from "/@src/containers/pages/FeatureComparison";
import IntlMessages from "/@src/helpers/IntlMessages";
import { api } from "/@src/network/auth";
import { useBillingContext } from "../BillingProvider";
import { NotificationManager } from "/@src/components/common/react-notifications";
import { NavLink } from "react-router-dom";

function Plans({ currentPlan, upgradePlanTo = "" }) {
  const { standardPlans } = useSelector((state) => state.billingAndPlans);
  const { currentUser } = useSelector(({ authUser }) => authUser);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const { changePlan } = useBillingContext();

  const currentUserPlan = currentUser.plan;
  const all = getMappedPlansAndFeatures(standardPlans, currentUserPlan);
  const subscriptionOptions = [
    {
      label: "Monthly",
      value: "monthly",
      key: 1,
    },
    {
      label: "Yearly",
      value: "yearly",
      key: 2,
    },
  ];
  const currencyList = ["USD", "INR", "EUR"];
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState(
    subscriptionOptions[0]
  );
  const [currency, setCurrency] = useState("USD");
  const manageBilling = async (data) => {
    setLoading(true);

    try {
      await changePlan(data, selectedSubscriptionType.value);
    } catch (e) {
      console.error(e);
      NotificationManager.error("Unable to change plan", "Plan Change Failed");
    }

    setLoading(false);
  };

  const pricingFeatures = all.map((plan) => {
    const selectedPricing = plan.prices.find((p) => p.currency === currency);
    const currentPrice = selectedPricing?.prices.find(
      (p) => p.type === selectedSubscriptionType.value
    )?.value;
    const oldPrice = selectedPricing?.prices.find(
      (p) => p.type === selectedSubscriptionType.value
    )?.oldPrice;
    return {
      title: plan.name,
      link: `${appRoot}/manageBilling`,
      price: selectedPricing
        ? `${selectedPricing?.currencySymbol}${currentPrice ?? "N/A"}`
        : "N/A",
      oldPrice: oldPrice ? selectedPricing?.currencySymbol + oldPrice : null,
      ...plan,
    };
  });

  const getFeatureComparisonData = () => {
    const featureComparisonWithPlans = all.map((p) => {
      const {
        name,
        features: { featureComparison },
      } = p;
      return { name, featureComparison };
    });

    const allFeatures = featureComparisonWithPlans
      .flatMap((plan) =>
        plan.featureComparison.map((feature) => {
          return { key: feature.key, name: feature.name, value: feature.value };
        })
      )
      .filter((f) => f.value);
    // Remove duplicate features
    const uniqueFeatures = uniqWith(allFeatures, isEqual);

    // Create a mapping of uniqueFeatures to plans
    const mappedFeatures = uniqueFeatures.map((f) => {
      const allPlans = [];
      all.forEach((p) => {
        const t = p.features.featureComparison
          .filter((v) => v.value)
          .map((p) => p.key);
        if (t.includes(f.key)) {
          allPlans.push(p.name);
        }
      });
      return { feature: f, plans: allPlans };
    });

    // Extract all plan names
    const allPlans = all.map((p) => {
      return { name: p.name };
    });

    return { allPlans, features: mappedFeatures };
  };
  const featureComparisonData = getFeatureComparisonData();
  return (
    <>
      <Row className="my-2">
        <Colxx xxs="10">
          <h3>
            Current Plan:{" "}
            {currentUser?.plan && (
              <span className="text-italic text-primary">
                {currentUser?.plan?.name || "Add Plan"}
              </span>
            )}
            {!currentUser?.plan && (
              <button className="text-italic text-primary">Add Plan</button>
            )}
          </h3>
          {currentUser?.plan && (
            <Row>
              <Button className="ml-3" outline onClick={manageBilling}>
                Payment History
              </Button>
            </Row>
          )}
        </Colxx>
      </Row>
      <hr />
      <Row className="">
        <Colxx xxs="10">
          <CardTitle>Plans & Pricing</CardTitle>
        </Colxx>
      </Row>
      {/* <Row className="m-2 p-4">
        <Alert color="primary w-100" className="text-center m-auto">
          <div style={{fontSize: '1.4rem'}}>&#127881; 2024 Deal. Flat 50% OFF on all plans. Use code <strong>NY2024</strong> &#127881;</div>
          <div className="text-default">
            <em>Offer valid till Jan 31!!</em>
          </div>
        </Alert>
      </Row> */}
      <Row>
        <Colxx className="text-center">
          <ButtonGroup>
            <Button
              outline
              active={selectedSubscriptionType.label === "Monthly"}
              onClick={() =>
                setSelectedSubscriptionType(subscriptionOptions[0])
              }
            >
              Monthly
            </Button>
            <Button
              outline
              active={selectedSubscriptionType.label === "Yearly"}
              onClick={() =>
                setSelectedSubscriptionType(subscriptionOptions[1])
              }
            >
              Yearly
            </Button>
          </ButtonGroup>
          {/* Todo: Uncomment when multiple currencies supported */}
          {/* <ButtonGroup className="ml-5">
            {currencyList.map((c) => (
              <Button
                outline
                active={currency === c}
                onClick={() => setCurrency(c)}
              >
                {c}
              </Button>
            ))}
          </ButtonGroup> */}
        </Colxx>
      </Row>
      {loading && (
        <div className="loader">
          <div className="loading" />
        </div>
      )}
      <Row
        className="equal-height-container my-4 flex-nowrap p-2"
        style={{ width: "100%", overflow: "auto" }}
      >
        {pricingFeatures.map((plan, index) => {
          const recommended = plan.name === upgradePlanTo;
          return (
            <Colxx
              xs="6"
              lg="3"
              className="col-item my-4"
              style={{ minWidth: "250px" }}
              key={`priceCard_${index}`}
            >
              <PriceCard
                onPurchase={manageBilling}
                isCurrentPlan={currentPlan === plan.title}
                data={plan}
                recommended={recommended}
                subscriptionType={selectedSubscriptionType.label}
              />
            </Colxx>
          );
        })}
      </Row>
      <Row>
        <Colxx xxs="12">
          <CardTitle>
            <IntlMessages id="pages.prices.featurecomparison" />
          </CardTitle>
        </Colxx>
        <FeatureComparison data={featureComparisonData} />
      </Row>
    </>
  );
}

export default Plans;
